import React, { createContext, useContext, useState } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext();

const normalizePlan = (plan) => {
  if (!plan) return 'basic';
  const p = plan.toLowerCase();
  if (p.includes('basic') || p.includes('explor')) return 'basic';
  if (p.includes('premium') || p.includes('navigat')) return 'premium';
  if (p.includes('enterprise') || p.includes('pathfinder') || p.includes('pionnier')) return 'enterprise';
  return 'basic';
};

// Read synchronously so protected routes don't redirect to sign-in on a page refresh.
const loadSession = () => {
  try {
    const token = localStorage.getItem('ig_token');
    const parsed = JSON.parse(localStorage.getItem('ig_user') || 'null');
    if (token && parsed) return { ...parsed, plan: normalizePlan(parsed.plan || parsed.accessLevel) };
  } catch {
    localStorage.removeItem('ig_user');
  }
  return null;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(loadSession);
  const [loading, setLoading] = useState(false);
  const isAuthenticated = !!user;

  const saveSession = (token, rawUser) => {
    const newUser = { ...rawUser, plan: normalizePlan(rawUser.plan || rawUser.accessLevel) };
    localStorage.setItem('ig_token', token);
    localStorage.setItem('ig_user', JSON.stringify(newUser));
    setUser(newUser);
    return newUser;
  };

  const register = async (firstName, lastName, email, password, country, phone, paymentReference) => {
    setLoading(true);
    try {
      const response = await authAPI.register({ firstName, lastName, email, password, country, phone, paymentReference });
      return saveSession(response.data.token, response.data.user);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    setLoading(true);
    try {
      const response = await authAPI.login(email, password);
      return saveSession(response.data.token, response.data.user);
    } finally {
      setLoading(false);
    }
  };

  // Upgrade or renew with a confirmed payment made for the new plan.
  const upgrade = async (paymentReference) => {
    const response = await authAPI.upgrade(paymentReference);
    return saveSession(response.data.token, response.data.user);
  };

  const logout = () => {
    localStorage.removeItem('ig_token');
    localStorage.removeItem('ig_user');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, isAuthenticated, register, login, upgrade, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
